import { ReactElement, useMemo, useCallback } from 'react';
import { Stack, Tooltip } from '@mui/material';
import {
  DoneOutlined as DoneIcon,
  CloseOutlined as CancelIcon,
} from '@mui/icons-material';
import {
  faMagnifyingGlass as SearchIcon,
  faFilter as FilterIcon,
  faArrowsRotate as RefreshIcon,
} from '@fortawesome/free-solid-svg-icons';

import { SelectOption } from '~globals/types/commons';
import { useToggleMenu } from '~hooks/useToggleMenu';
import OrdersFilterMenuGiver from '~components/OrdersFilterMenuGiver';
import { ORDERS_FINALIZED_STATE_OPTIONS } from '~constants/ordersStatesOptions';

// eslint-disable-next-line max-len
import { useDashboardVariantV2GiverFinalizedFiltersContext } from '../../../../../../FinalizedFiltersContext';
import { useDashboardVariantV2GiverContentFinalizedTabContext } from '../../../Context';

import {
  DashboardVariantV2GiverContentActionsFieldWithButtonGroup,
  DashboardVariantV2GiverContentActionsTextField,
  DashboardVariantV2GiverContentActionsIconButton,
  DashboardVariantV2GiverContentActionsIconButtonIcon,
  DashboardVariantV2GiverContentActionsButton,
} from '../../../../../styles';

const [COMPLETED_STATE_OPTION, INCOMPLETED_STATE_OPTION] =
  ORDERS_FINALIZED_STATE_OPTIONS;

const DashboardVariantV2GiverContentFinalizedTabActionsFilters =
  (): ReactElement => {
    const {
      autocompletesRefs,
      filters: {
        filters,
        filtersActives,
        setFilterByKey,
        handleChangeInputFilter,
        handleApplyFilter,
        handleResetFilters,
        handleClearFilters,
        handleClearFilterByKey,
      },
      forceRequest: { toggleValue: toggleForceRequest },
    } = useDashboardVariantV2GiverFinalizedFiltersContext();

    const {
      actions: { reloadDataAndResetSelectedRows },
    } = useDashboardVariantV2GiverContentFinalizedTabContext();

    const [
      anchorElOrdersFiltersMenu,
      openOrdersFiltersMenu,
      ,
      handleToggleWithValue,
    ] = useToggleMenu();

    const currentItemStates = useMemo(
      () => filtersActives?.itemState ?? [],
      [filtersActives?.itemState],
    );

    const isAppliedFilterItemState = useCallback(
      (selectedState: SelectOption) =>
        currentItemStates.some((state) => state.value === selectedState.value),
      [currentItemStates],
    );

    const getVariantButtonToggleItemState = useCallback(
      (selectedState: SelectOption) =>
        isAppliedFilterItemState(selectedState) ? 'contained' : 'outlined',
      [isAppliedFilterItemState],
    );

    const handleToogleFilterItemStates = useCallback(
      (selectedState: SelectOption) => {
        handleClearFilterByKey('itemState', [selectedState], () =>
          toggleForceRequest(true),
        );

        setTimeout(() => {
          autocompletesRefs.itemState.current?.setValue([selectedState]);
        }, 150);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [handleClearFilterByKey, toggleForceRequest],
    );

    return (
      <Stack direction="row" spacing={2}>
        <Stack direction="row" spacing={1}>
          <DashboardVariantV2GiverContentActionsFieldWithButtonGroup>
            <DashboardVariantV2GiverContentActionsTextField
              label="Nro. Orden / Nro. Tracking / Grupo"
              name="search"
              fullWidth
              value={filters.search ?? ''}
              onChange={handleChangeInputFilter}
            />

            <Tooltip title="Buscar..." arrow>
              <span style={{ display: 'flex' }}>
                <DashboardVariantV2GiverContentActionsIconButton
                  color="secondary"
                  variant="contained"
                  onClick={handleApplyFilter}
                  disabled={!filters.search}
                >
                  <DashboardVariantV2GiverContentActionsIconButtonIcon
                    icon={SearchIcon}
                  />
                </DashboardVariantV2GiverContentActionsIconButton>
              </span>
            </Tooltip>
          </DashboardVariantV2GiverContentActionsFieldWithButtonGroup>

          <Tooltip title="Mas filtros" arrow>
            <DashboardVariantV2GiverContentActionsIconButton
              color="primary"
              variant="contained"
              ref={anchorElOrdersFiltersMenu}
              onClick={() => handleToggleWithValue(true)}
            >
              <DashboardVariantV2GiverContentActionsIconButtonIcon
                icon={FilterIcon}
              />
            </DashboardVariantV2GiverContentActionsIconButton>
          </Tooltip>

          <Tooltip title="Refrescar listado" arrow>
            <DashboardVariantV2GiverContentActionsIconButton
              color="secondary"
              variant="outlined"
              onClick={reloadDataAndResetSelectedRows}
            >
              <DashboardVariantV2GiverContentActionsIconButtonIcon
                icon={RefreshIcon}
              />
            </DashboardVariantV2GiverContentActionsIconButton>
          </Tooltip>

          <OrdersFilterMenuGiver
            anchorEl={anchorElOrdersFiltersMenu.current}
            anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
            transformOrigin={{ horizontal: 'center', vertical: 'top' }}
            open={openOrdersFiltersMenu}
            onClose={() => handleToggleWithValue(false)}
            type="finalized"
            currentFilters={{
              autocompletesRefs,
              filters,
              activeFilters: filtersActives,
              onChangeFilterInput: setFilterByKey,
              onResetFilters: handleResetFilters,
              onApplyFilters: handleApplyFilter,
              onClearFilters: () => handleClearFilters(true),
            }}
          />
        </Stack>

        <Stack direction="row" spacing={1}>
          <DashboardVariantV2GiverContentActionsButton
            variant={getVariantButtonToggleItemState(COMPLETED_STATE_OPTION)}
            color="success"
            startIcon={<DoneIcon />}
            onClick={() => handleToogleFilterItemStates(COMPLETED_STATE_OPTION)}
          >
            Pedidos Cumplidos
          </DashboardVariantV2GiverContentActionsButton>

          <DashboardVariantV2GiverContentActionsButton
            variant={getVariantButtonToggleItemState(INCOMPLETED_STATE_OPTION)}
            color="error"
            startIcon={<CancelIcon />}
            onClick={() =>
              handleToogleFilterItemStates(INCOMPLETED_STATE_OPTION)
            }
          >
            Pedidos Con incidencias
          </DashboardVariantV2GiverContentActionsButton>
        </Stack>
      </Stack>
    );
  };

export default DashboardVariantV2GiverContentFinalizedTabActionsFilters;
